import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import Navbar from '../components/ui/Navbar';

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Briefcase, CheckCircle2, Search, Clock, Camera, 
  FileCheck, ImageIcon, ShieldAlert, 
  FileText, Upload, MapPin, Loader2, XCircle, ArrowRight, Phone, ShieldCheck, CalendarDays
} from 'lucide-react';

import planoFundo from "../assets/planodefundo.png";

const BecomeTechnician = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [dbFunctions, setDbFunctions] = useState([]);
  const [searchCategory, setSearchCategory] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [acceptLGPD, setAcceptLGPD] = useState(false);

  const [errors, setErrors] = useState({});
  const [validating, setValidating] = useState({});

  const [formData, setFormData] = useState({ 
    areas: [], bio: '', technicianDocument: '', technicianName: '', technicianPhone: ''
  });

  const [techAddress, setTechAddress] = useState({
    cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: ''
  });

  const [tecPhotoFile, setTecPhotoFile] = useState(null);
  const [docFrontFile, setDocFrontFile] = useState(null);
  const [docBackFile, setDocBackFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [criminalFile, setCriminalFile] = useState(null);

  // --- MÁSCARAS ---
  const maskPhone = (v) => v.replace(/\D/g, "").substring(0, 11).replace(/^(\d{2})(\d{5})(\d{4})/, "($1) $2-$3").replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
  const maskCpfCnpj = (v) => {
    let r = v.replace(/\D/g, "").substring(0, 14);
    if (r.length <= 11) return r.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    return r.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  };
  const maskCep = (v) => v.replace(/\D/g, "").substring(0, 8).replace(/(\d{5})(\d)/, "$1-$2");

  useEffect(() => {
    if (userProfile?.professionalData) {
      const p = userProfile.professionalData;
      setFormData({
        areas: p.areas || [], bio: p.bio || '',
        technicianDocument: maskCpfCnpj(p.document || ''),
        technicianName: p.name || '',
        technicianPhone: maskPhone(p.phone || '')
      });
      if (p.address) setTechAddress(p.address);
    }
  }, [userProfile]);

  useEffect(() => {
    const fetchFunctions = async () => {
      const snap = await getDocs(query(collection(db, "functions")));
      setDbFunctions(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(f => f.active !== false).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    };
    fetchFunctions();
  }, []);

  // --- VERIFICADOR DE DUPLICIDADE CPF/CNPJ ---
  const handleDocBlur = async () => {
    const clean = formData.technicianDocument.replace(/\D/g, "");
    if (!clean || clean.length < 11) return;

    setValidating(p => ({ ...p, doc: true }));
    try {
      // Busca usuários que já tenham este documento cadastrado como PROFISSIONAL
      const q = query(collection(db, "users"), where("professionalData.document", "==", clean));
      const snap = await getDocs(q);
      
      // Bloqueia apenas se encontrar outro usuário (ID diferente do atual) com esse documento
      const isDuplicate = !snap.empty && snap.docs.some(d => d.id !== user.uid);
      
      setErrors(p => ({ ...p, doc: isDuplicate }));
      if (isDuplicate) toast.error("Este CPF/CNPJ já está vinculado a outro profissional!");
    } catch (e) {
      console.error(e);
    } finally {
      setValidating(p => ({ ...p, doc: false }));
    }
  };

  const handleCepBlur = async () => {
    const cleanCep = techAddress.cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;
    setValidating(p => ({ ...p, cep: true }));
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setTechAddress(prev => ({ 
            ...prev, street: data.logradouro.toUpperCase(), 
            neighborhood: data.bairro.toUpperCase(), city: data.localidade.toUpperCase(), 
            state: data.uf.toUpperCase() 
        }));
      }
    } finally { setValidating(p => ({ ...p, cep: false })); }
  };

  const handleFinalSubmit = async () => {
    if (!acceptLGPD) return toast.error("Aceite os termos da LGPD.");
    if (errors.doc) return toast.error("Corrija o CPF/CNPJ duplicado.");
    
    setShowConfirmModal(false);
    setLoading(true);
    try {
      const uploadFile = async (file, folder, existingUrl) => {
        if (!file) return existingUrl || "";
        const storageRef = ref(storage, `verification/${user.uid}/${folder}_${Date.now()}`);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
      };

      const [frontUrl, backUrl, selfieUrl, criminalUrl, tecPhotoUrl] = await Promise.all([
        uploadFile(docFrontFile, 'documento_frente', userProfile?.verificationDocs?.documentFrontUrl),
        uploadFile(docBackFile, 'documento_verso', userProfile?.verificationDocs?.documentBackUrl),
        uploadFile(selfieFile, 'selfie_presencial', userProfile?.verificationDocs?.selfieUrl),
        uploadFile(criminalFile, 'antecedentes_criminais', userProfile?.verificationDocs?.criminalRecordUrl),
        uploadFile(tecPhotoFile, 'perfil_tecnico', userProfile?.professionalData?.photoUrl)
      ]);

      await updateDoc(doc(db, 'users', user.uid), {
        userType: 'technician',
        technicianStatus: 'pending',
        rejectionReason: "", 
        professionalData: {
          areas: formData.areas, bio: formData.bio, name: formData.technicianName,
          phone: formData.technicianPhone.replace(/\D/g, ""),
          document: formData.technicianDocument.replace(/\D/g, ""),
          photoUrl: tecPhotoUrl, address: techAddress,
          lgpdAccepted: true, lgpdDate: new Date().toISOString(),
          subscriptionPrice: 49.90, subscriptionType: 'monthly'
        },
        verificationDocs: {
          documentFrontUrl: frontUrl, documentBackUrl: backUrl,
          selfieUrl: selfieUrl, criminalRecordUrl: criminalUrl,
          submittedAt: new Date().toISOString()
        },
        updatedAt: new Date().toISOString()
      });

      toast.success('Inscrição enviada!');
      navigate('/dashboard'); 
    } catch (error) { toast.error('Erro ao salvar.'); }
    finally { setLoading(false); }
  };

  const getInputClass = (field, value, errorKey) => {
    const base = "h-14 rounded-2xl border-2 transition-all bg-white shadow-inner font-bold uppercase ";
    if (validating[field]) return base + "border-blue-400 animate-pulse";
    if (errors[errorKey || field]) return base + "border-red-500 bg-red-50/30";
    if (value && !errors[errorKey || field]) return base + "border-green-500 bg-green-50/10";
    return base + "border-transparent focus:border-orange-500";
  };

  if (authLoading) return null;

  return (
    <div className="relative min-h-screen font-sans text-slate-900 bg-slate-50 text-left">
      <div className="fixed inset-0 -z-10 w-full h-full opacity-15" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
      <Navbar />

      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-20">
        <div className="mb-10 text-left">
            <Badge className="bg-orange-500 text-white font-black text-[9px] px-4 py-1 mb-4 border-none uppercase italic shadow-lg">Credenciamento Profissional</Badge>
            <h1 className="text-4xl md:text-7xl font-black text-slate-900 uppercase italic tracking-tighter leading-none">SEJA UM <span className="text-orange-500">PARCEIRO</span></h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] flex items-center gap-6 shadow-2xl border-t-8 border-orange-500 relative overflow-hidden">
                <div className="w-16 h-16 bg-orange-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg text-white"><CalendarDays size={32} /></div>
                <div>
                    <p className="text-[10px] font-black uppercase text-orange-400 tracking-widest mb-1 italic">Assinatura Mensal</p>
                    <h3 className="text-3xl font-black italic tracking-tighter leading-none">R$ 49,90</h3>
                    <p className="text-[9px] text-slate-400 font-bold uppercase mt-2 italic">Acesso à rede profissional com chamados ilimitados.</p>
                </div>
            </div>
            <div className="bg-white p-8 rounded-[2.5rem] border border-blue-100 flex items-center gap-6 shadow-xl">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center shrink-0 text-blue-600"><ShieldCheck size={32} /></div>
                <div>
                    <p className="text-[10px] font-black uppercase text-blue-600 tracking-widest mb-1 italic">Conformidade Legal</p>
                    <h3 className="text-xl font-black italic text-slate-800 leading-none uppercase">LGPD 100% ATIVA</h3>
                    <p className="text-[9px] text-slate-400 font-bold uppercase mt-2 italic">Seus documentos são criptografados e não compartilhados.</p>
                </div>
            </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); if (formData.areas.length === 0) return toast.error('Escolha sua especialidade!'); setShowConfirmModal(true); }} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-8">
            <Card className="rounded-[3rem] border-none shadow-2xl bg-white p-8 md:p-10 space-y-8 border-t-8 border-orange-500">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                <div className="flex flex-col items-center gap-3">
                  <Label className="text-[9px] font-black uppercase text-slate-400 italic">Avatar Profissional</Label>
                  <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => setTecPhotoFile(e.target.files[0])} className="hidden" id="tec-photo" />
                  <label htmlFor="tec-photo" className={`group w-32 h-32 rounded-full border-4 border-dashed transition-all flex flex-col items-center justify-center overflow-hidden cursor-pointer ${tecPhotoFile || userProfile?.professionalData?.photoUrl ? 'border-green-500 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                    {(tecPhotoFile || userProfile?.professionalData?.photoUrl) ? (
                      <img src={tecPhotoFile ? URL.createObjectURL(tecPhotoFile) : userProfile.professionalData.photoUrl} className="w-full h-full object-cover" alt="Preview" />
                    ) : <Upload className="text-slate-300" />}
                  </label>
                  <p className="text-[7px] font-black text-slate-300 uppercase">JPG, PNG apenas</p>
                </div>
                <div className="md:col-span-2 space-y-6">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-black text-slate-400 uppercase italic ml-2">Nome Comercial *</Label>
                    <Input className="h-14 rounded-2xl border-2 border-transparent bg-slate-50 focus:border-orange-500 font-bold uppercase" value={formData.technicianName} onChange={(e) => setFormData({...formData, technicianName: e.target.value.toUpperCase()})} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1 text-left">
                        <Label className="text-[10px] font-black text-slate-400 uppercase italic ml-2">WhatsApp *</Label>
                        <Input className="h-14 rounded-2xl border-2 border-transparent bg-slate-50 focus:border-orange-500 font-bold" value={formData.technicianPhone} onChange={(e) => setFormData({...formData, technicianPhone: maskPhone(e.target.value)})} required />
                    </div>
                    <div className="space-y-1 relative text-left">
                        <Label className="text-[10px] font-black text-slate-400 uppercase italic ml-2">CPF ou CNPJ *</Label>
                        <Input className={getInputClass('doc', formData.technicianDocument)} value={formData.technicianDocument} onBlur={handleDocBlur} onChange={(e) => {setFormData({...formData, technicianDocument: maskCpfCnpj(e.target.value)}); setErrors(p=>({...p, doc: false}))}} required />
                        {validating.doc && <Loader2 className="absolute right-4 top-10 animate-spin text-blue-500 w-4 h-4" />}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6 pt-6 border-t border-slate-50">
                <Label className="text-[11px] font-black uppercase text-slate-800 italic flex items-center gap-2"><MapPin size={16} className="text-orange-500" /> Onde você atende?</Label>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input value={techAddress.cep} placeholder="CEP" onBlur={handleCepBlur} onChange={e => setTechAddress({...techAddress, cep: maskCep(e.target.value)})} className="h-14 rounded-2xl bg-slate-50 border-none font-bold" required />
                  <Input value={techAddress.street} placeholder="Rua" className="md:col-span-2 h-14 rounded-2xl bg-slate-50 border-none font-bold uppercase" readOnly />
                  <Input value={techAddress.number} placeholder="Nº" onChange={e => setTechAddress({...techAddress, number: e.target.value})} className="h-14 rounded-2xl bg-slate-50 border-none font-bold text-center shadow-inner" required />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Input value={techAddress.neighborhood} placeholder="Bairro" className="h-14 rounded-2xl bg-slate-50 border-none font-bold italic" readOnly />
                  <Input value={techAddress.city} disabled className="h-14 rounded-2xl bg-slate-100 border-none font-black italic text-orange-600 px-6 uppercase" />
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-slate-50">
                <Label className="text-[11px] font-black uppercase text-slate-800 italic ml-2">Especialidades *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[180px] overflow-y-auto custom-scrollbar">
                  {dbFunctions.map((f) => (
                    <div key={f.id} onClick={() => setFormData(prev => ({ ...prev, areas: prev.areas.includes(f.name) ? prev.areas.filter(a => a !== f.name) : [...prev.areas, f.name] }))} 
                      className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all cursor-pointer ${formData.areas.includes(f.name) ? 'border-orange-500 bg-orange-50' : 'border-slate-50 bg-slate-50/50 hover:bg-orange-50'}`}>
                      <span className="font-black uppercase italic text-[10px] text-slate-700">{f.name}</span>
                      {formData.areas.includes(f.name) && <CheckCircle2 className="w-5 h-5 text-orange-500" />}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2 pt-4">
                <Label className="text-[10px] font-black text-slate-400 uppercase italic ml-2">Bio Profissional *</Label>
                <Textarea className="min-h-[120px] rounded-[2.5rem] bg-slate-50 border-none font-bold italic text-slate-700 p-8 shadow-inner" value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} required />
              </div>
            </Card>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <Card className="rounded-[3rem] border-none shadow-2xl bg-slate-900 text-white p-8 space-y-8 border-t-8 border-orange-500">
              <h2 className="text-xl font-black uppercase italic flex items-center gap-3 tracking-tighter leading-none"><ShieldAlert className="text-orange-500" /> Verificação</h2>
              
              <div className="bg-white/5 p-6 rounded-[2rem] border border-white/10 space-y-4">
                 <div className="flex items-start gap-4 text-left">
                    <div className={`w-6 h-6 rounded-md border-2 shrink-0 flex items-center justify-center cursor-pointer transition-all ${acceptLGPD ? 'bg-orange-500 border-orange-500' : 'border-white/20'}`} onClick={() => setAcceptLGPD(!acceptLGPD)}>
                        {acceptLGPD && <CheckCircle2 size={14} className="text-white" />}
                    </div>
                    <div className="text-left">
                        <Label className="text-[10px] font-black uppercase text-white cursor-pointer" onClick={() => setAcceptLGPD(!acceptLGPD)}>Autorização LGPD</Label>
                        <p className="text-[8px] text-slate-400 font-bold leading-tight mt-1">Concordo com a assinatura mensal de R$ 49,90 e autorizo o processamento dos meus dados pessoais (Lei 13.709/2018).</p>
                    </div>
                 </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { id: 'front-up', file: docFrontFile, set: setDocFrontFile, label: 'Doc. Frente', exists: !!userProfile?.verificationDocs?.documentFrontUrl },
                  { id: 'back-up', file: docBackFile, set: setDocBackFile, label: 'Doc. Verso', exists: !!userProfile?.verificationDocs?.documentBackUrl }
                ].map(docItem => (
                  <div key={docItem.id} className="text-center">
                    <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => docItem.set(e.target.files[0])} className="hidden" id={docItem.id} />
                    <label htmlFor={docItem.id} className={`flex flex-col items-center justify-center h-28 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${docItem.file || docItem.exists ? 'border-orange-500 bg-orange-500/10' : 'border-white/10'}`}>
                      {docItem.file || docItem.exists ? <FileCheck className="text-orange-500" /> : <ImageIcon className="text-slate-600" />}
                      <span className="text-[8px] font-black uppercase mt-2 italic">{docItem.file ? 'Trocar' : docItem.exists ? 'Salvo' : docItem.label}</span>
                    </label>
                  </div>
                ))}
              </div>

              <div className="space-y-4 text-left">
                {[
                  { id: 'selfie-up', file: selfieFile, set: setSelfieFile, label: 'Selfie com Documento', icon: Camera, exists: !!userProfile?.verificationDocs?.selfieUrl, accept: ".jpg,.jpeg,.png" },
                  { id: 'criminal-up', file: criminalFile, set: setCriminalFile, label: 'Antecedentes (JPG ou PDF)', icon: FileText, exists: !!userProfile?.verificationDocs?.criminalRecordUrl, accept: ".jpg,.jpeg,.png,.pdf" }
                ].map(docItem => (
                  <div key={docItem.id}>
                    <input type="file" accept={docItem.accept} onChange={(e) => docItem.set(e.target.files[0])} className="hidden" id={docItem.id} />
                    <label htmlFor={docItem.id} className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${docItem.file || docItem.exists ? 'border-orange-500 bg-orange-500/10' : 'border-white/10'}`}>
                      <docItem.icon className="text-orange-500 mb-2" size={32} />
                      <span className="text-[9px] font-black uppercase italic text-center">{docItem.file ? docItem.file.name : docItem.exists ? 'Arquivo Atual' : docItem.label}</span>
                    </label>
                  </div>
                ))}
              </div>

              <Button type="submit" disabled={loading || !acceptLGPD || errors.doc} className="w-full h-20 bg-orange-600 hover:bg-orange-700 text-white font-black rounded-3xl uppercase italic text-sm shadow-xl transition-all">
                {loading ? <Loader2 className="animate-spin" /> : "FINALIZAR CADASTRO"}
              </Button>
            </Card>
          </div>
        </form>

        <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
          <DialogContent className="rounded-[2.5rem] border-none p-10 max-w-md bg-white text-left">
            <DialogHeader className="text-left">
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-6 shadow-inner text-orange-600"><ShieldCheck size={30} /></div>
              <DialogTitle className="text-3xl font-black uppercase italic tracking-tighter text-slate-900 leading-none">Assinar e Enviar?</DialogTitle>
            </DialogHeader>
            <div className="py-6 border-y border-slate-100 my-4 text-left">
              <p className="text-[11px] font-bold text-slate-500 leading-relaxed uppercase italic">Sua assinatura de R$ 49,90 mensal garantirá seu perfil ativo na rede de profissionais assim que aprovado.</p>
            </div>
            <DialogFooter className="flex flex-col gap-3">
              <Button onClick={handleFinalSubmit} className="w-full h-16 bg-orange-600 hover:bg-orange-700 text-white font-black rounded-2xl uppercase italic text-xs shadow-xl">CONFIRMAR INSCRIÇÃO</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; }` }} />
    </div>
  );
};

export default BecomeTechnician;